<template>
  <div
    class="fullCalendar"
    :class="`${$mq}Mode`"
  >
    <FullCalendar
      ref="fullCalendar"
      :default-view="fullCalendarView"
      :header="header"
      :plugins="calendarPlugins"
      :events="calendarEvents"
      :event-limit="true"
      :event-time-format="eventTimeFormat"
      :slot-label-format="slotLabelFormat"
      :all-day-slot="false"
      :scroll-time="'08:30:00'"
      :first-day="1"
      :now-indicator="true"
      @eventClick="handleEventClick"
      @datesRender="datesRender"
    />

    <EventModal :selected-event.sync="selectedEvent" />
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import EventModal from './EventModal.vue';

export default {
  components: {
    FullCalendar,
    EventModal,
  },

  props: {
    view: {
      type: String,
      default: 'month',
    },

    calendarEvents: {
      type: Array,
      default() {
        return [];
      },
    },

    activeStart: {
      type: String,
      default: '',
    },

    activeEnd: {
      type: String,
      default: '',
    },

    placedDate: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      header: {
        left: 'today',
        center: 'title',
        right: 'prev,next',
      },
      calendarPlugins: [
        dayGridPlugin,
        timeGridPlugin,
        interactionPlugin,
        listPlugin,
      ],
      selectedEvent: null,
      eventTimeFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
      slotLabelFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
    };
  },

  computed: {
    fullCalendarView() {
      let view;
      switch (this.view) {
        case 'month':
          view = 'dayGridMonth';
          break;
        case 'week':
          view = 'timeGridWeek';
          break;
        case 'list':
          view = 'listMonth';
          break;
        default: view = '';
      }
      return view;
    },
  },

  watch: {
    fullCalendarView(newView) {
      if (this.$refs.fullCalendar) {
        this.$refs.fullCalendar.getApi().changeView(newView);
      }
    },

    placedDate(newDate) {
      if (this.$refs.fullCalendar) {
        this.$refs.fullCalendar.getApi().gotoDate(newDate);
      }
    },
  },

  methods: {
    handleEventClick(arg) {
      this.selectedEvent = arg.event;
    },

    datesRender(arg) {
      const viewActiveStart = this.$moment(arg.view.activeStart).format('YYYY-MM-DD');
      const viewActiveEnd = this.$moment(arg.view.activeEnd).format('YYYY-MM-DD');

      if (viewActiveStart !== this.activeStart || viewActiveEnd !== this.activeEnd) {
        this.$emit('update:activeStart', viewActiveStart);
        this.$emit('update:activeEnd', viewActiveEnd);
        this.$emit('datesRender', {
          startDate: viewActiveStart,
          endDate: viewActiveEnd,
        });
      }
    },
  },
};
</script>

<style lang='scss'>
@import '~@fullcalendar/core/main.css';
@import '~@fullcalendar/daygrid/main.css';
@import '~@fullcalendar/timegrid/main.css';
@import '~@fullcalendar/list/main.css';

.fullCalendar {
  .fc-toolbar {
    font-size: 18px;

    h2 {
      font-size: 1em;
    }

    .fc-button {
      background-color: transparent;
      border-color: transparent;
      box-shadow: none;
      color: var(--themeFontColor);

      &.fc-today-button {
        font-size: 16px;
        text-transform: capitalize;
      }
    }

    &.fc-header-toolbar {
      margin-bottom: 0;
    }
  }

  .fc {
    .fc-scroller:not(.fc-day-grid-container) {
      min-height: 525px;
    }

    .fc-scroller.fc-day-grid-container {
      height: 100% !important;
    }

    .fc-view-container {
      background-color: white;
      color: black;

      .fc-head {
        .fc-widget-header {
          .fc-day-header {
            font-weight: normal;
            font-size: 12px;
            height: 25px;
            padding: 2px 0;
            vertical-align: middle;
          }
        }
      }

      .fc-list-item,
      .fc-event {
        cursor: pointer;
      }

      .fc-list-item {
        line-height: 1.5em;
      }

      .fc-dayGrid-view {
        .fc-day-number {
          font-size: 14px;
          padding: 5px;
        }

        .fc-title {
          font-size: 14px;
        }
      }

      .fc-timeGrid-view {
        // below code break week view event layout
        // .fc-content {
        //   display: flex;
        //   align-items: baseline;
        //   height: 100%;
        //   padding-top: 1px;
        //   box-sizing: border-box;
        // }

        .fc-time {
          font-size: 12px;
          // text-align: center;
        }

        .fc-title {
          font-size: 14px;
        }
      }

      .fc-more-popover {
        width: 60%;
        min-width: 225px;
        top: 50% !important;
        left: 50% !important;
        transform: translate(-50%, -50%) !important;

        .fc-widget-content {
          max-height: 230px;
          overflow: auto;
        }
      }
    }

    &.fc-ltr .fc-dayGrid-view .fc-day-top .fc-day-number {
      float: left;
    }

    .fc-center {
      h2 {
        font-size: 1.25rem;
      }
    }
  }

  &.tabletMode {
    .fc {
      .fc-view-container {
        .fc-dayGrid-view {
          table {
            font-size: 0.95em;
          }
        }
      }
    }
  }

  &.mobileMode {
    .fc {
      .fc-scroller:not(.fc-day-grid-container) {
        min-height: 360px;
      }

      .fc-view-container {
        .fc-dayGrid-view {
          table {
            font-size: 0.85em;
          }
        }

        .fc-timeGrid-view {
          table {
            font-size: 0.85em;
          }
        }

        .fc-list-view {
          .fc-list-table {
            font-size: 0.85em;
          }
        }
      }
    }

    .fc-center {
      h2 {
        font-size: 1rem;
      }
    }
  }

  .formEvent {
    label,
    .el-input__inner {
      color: black !important;
    }
  }
}
</style>
